import React, { useState, useEffect, useRef } from 'react';
import { DateObject } from 'react-multi-date-picker';

type destinationProp = {
  label: string;
  options: {
    label: string;
    value: string;
    default: boolean;
  }[];
};

type DropdownProps = {
  id: string;
  label: string;
  options: destinationProp | string[];
  dropdownType: string;
  setSelectedValues: (selectedValues: { [key: string]: string | DateObject }) => void;
  selectedValues: { [key: string]: string | DateObject };
  position?: 'top' | 'bottom';
};

const Dropdown = ({
  id,
  label,
  options,
  dropdownType,
  setSelectedValues,
  selectedValues,
  position = 'bottom'
}: DropdownProps) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = (e: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
      setOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleOptionClick = (e: React.MouseEvent<HTMLElement>) => {
    setSelectedValues({
      ...selectedValues,
      [dropdownType]: (e.target as HTMLElement).dataset.value || ''
    });
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Escape') setOpen(false);
    if (e.key === 'Tab') setOpen(false);
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const options = Array.from(document.querySelectorAll('.booking-hero__select-option'));
      const currentIndex = options.findIndex(option => option === e.target);
      const nextIndex = e.key === 'ArrowDown' ? currentIndex + 1 : currentIndex - 1;
      const nextOption = options[nextIndex];
      if (nextOption) (nextOption as HTMLElement).focus();
    }
    if (e.key === 'Enter' || e.key === 'Space') {
      setSelectedValues({
        ...selectedValues,
        [dropdownType]: (e.target as HTMLElement).dataset.value || ''
      });
    }
  };

  return (
    <div
      ref={dropdownRef}
      className={`booking-hero__field booking-hero__field--select ${open ? 'open' : ''}`}
    >
      <p className="booking-hero__label">{label}</p>
      <div className="booking-hero__select-wrapper">
        <button
          className="booking-hero__select-button"
          aria-haspopup="true"
          aria-expanded="false"
          aria-controls={id}
          onClick={() => setOpen(prev => !prev)}
        >
          {dropdownType === 'destination'
            ? (options as destinationProp).options.find(
                option => option.value === selectedValues[dropdownType]
              )?.label
            : (options as string[])?.find((option: string) =>
                option.includes(selectedValues[dropdownType] as string)
              )}
        </button>
        <ul
          className={`booking-hero__select booking-hero__select--${position}`}
          id={id}
          onClick={handleOptionClick}
          onKeyDown={handleKeyDown}
        >
          {dropdownType === 'destination'
            ? (options as destinationProp).options.map(option => (
                <li
                  tabIndex={0}
                  key={option.value}
                  data-value={option.value}
                  className="booking-hero__select-option"
                >
                  {option.label}
                </li>
              ))
            : (options as string[]).map((option, index) => (
                <li
                  tabIndex={0}
                  key={index}
                  data-value={index + 1}
                  className="booking-hero__select-option"
                >
                  {option}
                </li>
              ))}
        </ul>
      </div>
    </div>
  );
};
export default Dropdown;
